<p-drawer
  [visible]="visible()"
  (visibleChange)="onDrawerVisibleChange($event)"
  position="right"
  appendTo="body"
  styleClass="sidebarlist1 usage-summary-drawer"
  header="Usage Summary"
  [closable]="true">
  <section class="usage-summary">
    <div class="usage-summary-filters" role="group" aria-label="Usage summary filters">
      <div class="usage-summary-filters__period usage-period-filters">
        @for (opt of usagePeriodOptions; track opt.value) {
          <button
            type="button"
            class="usage-period-chip"
            [class.usage-period-chip--active]="period() === opt.value"
            (click)="onPeriodChange(opt.value)">
            {{ opt.label }}
          </button>
        }
      </div>
      @if (showUserFilter()) {
        <div class="usage-summary-filters__select">
          <label class="usage-summary-filters__label" for="usage-summary-user">User</label>
          <p-multiSelect
            inputId="usage-summary-user"
            [options]="userFilterOptions()"
            optionLabel="label"
            optionValue="userId"
            [ngModel]="selectedUserIds()"
            (ngModelChange)="onUserFilterChange($event)"
            placeholder="All users"
            display="chip"
            [filter]="true"
            filterPlaceholder="Search users"
            [showToggleAll]="true"
            [maxSelectedLabels]="2"
            selectedItemsLabel="{0} users selected"
            appendTo="body"
            styleClass="usage-summary-select usage-summary-multiselect" />
        </div>
      }
      <div class="usage-summary-filters__select">
        <label class="usage-summary-filters__label" for="usage-summary-agent">Agent</label>
        <p-multiSelect
          inputId="usage-summary-agent"
          [options]="agentFilterOptions"
          optionLabel="label"
          optionValue="value"
          [ngModel]="selectedAgentCodes()"
          (ngModelChange)="onAgentFilterChange($event)"
          placeholder="All agents"
          display="chip"
          [filter]="true"
          filterPlaceholder="Search agents"
          [showToggleAll]="true"
          [maxSelectedLabels]="2"
          selectedItemsLabel="{0} agents selected"
          appendTo="body"
          styleClass="usage-summary-select usage-summary-multiselect" />
      </div>
    </div>

    @if (error()) {
      <p class="error-text">{{ error() }}</p>
    }

    @if (loading()) {
      <div class="metrics-loading" aria-busy="true" aria-live="polite">
        <p-progressSpinner strokeWidth="4" [style]="{ width: '32px', height: '32px' }" />
        <p class="hint-text">Loading usage summary…</p>
      </div>
    } @else if (summary(); as metrics) {
      <div class="usage-summary-kpis">
        <div class="usage-summary-kpi">
          <span class="usage-summary-kpi__label">Total tokens</span>
          <span class="usage-summary-kpi__value">{{ formatTokens(metrics.totalTokens) }}</span>
        </div>
        <div class="usage-summary-kpi">
          <span class="usage-summary-kpi__label">Active users</span>
          <span class="usage-summary-kpi__value">{{ metrics.topUsers.length }}</span>
        </div>
      </div>

      <div class="usage-metrics-panel usage-metrics-panel--full">
        <h4 class="usage-metrics-panel__title">Daily token consumption</h4>
        @let maxDaily = maxDailyTotal(metrics);
        <div class="daily-chart-wrap">
          @if (metrics.dailyByAgent.length) {
            <div class="daily-chart" role="img" aria-label="Daily token consumption by agent">
              @for (day of metrics.dailyByAgent; track day.date) {
                <div class="daily-chart__col">
                  <div
                    class="daily-chart__stack"
                    [style.height.px]="dailyColumnHeight(day, maxDaily)"
                    [attr.title]="dailyBarTooltip(day)">
                    @for (seg of dailyStackSegments(day); track seg.agentCode) {
                      <div
                        class="daily-chart__segment"
                        [style.height.%]="seg.heightPercent"
                        [style.background-color]="seg.color"
                        [pTooltip]="seg.agentName + ': ' + formatTokens(seg.tokens)"
                        tooltipPosition="top"></div>
                    }
                  </div>
                  <span class="daily-chart__label">{{ formatDayLabel(day.date) }}</span>
                </div>
              }
            </div>
            <ul class="daily-chart-legend">
              @for (agent of metrics.byAgent; track agent.agentCode) {
                <li class="daily-chart-legend__item">
                  <span
                    class="daily-chart-legend__dot"
                    [style.background-color]="agentChartColor(agent.agentCode)"></span>
                  <span class="daily-chart-legend__label">{{ agent.agentName }}</span>
                </li>
              }
            </ul>
          } @else {
            <p class="hint-text">No daily usage for the selected filters.</p>
          }
        </div>
      </div>

      <div class="usage-metrics-panels usage-metrics-panels--split">
        <div class="usage-metrics-panel usage-metrics-panel--agents">
          <h4 class="usage-metrics-panel__title">Tokens by Agent</h4>
          <ul class="agent-usage-list">
            @for (agent of metrics.byAgent; track agent.agentCode) {
              <li class="agent-usage-item">
                <span
                  class="agent-usage-item__dot"
                  [style.background-color]="agentChartColor(agent.agentCode)"></span>
                <div class="agent-usage-item__body">
                  <div class="agent-usage-item__row">
                    <span class="agent-usage-item__name">{{ agent.agentName }}</span>
                    <span class="agent-usage-item__tokens">{{ formatTokens(agent.tokens) }}</span>
                  </div>
                  <div class="agent-usage-item__bar" aria-hidden="true">
                    <div
                      class="agent-usage-item__bar-fill"
                      [style.width.%]="agentBarWidth(agent, metrics.totalTokens)"
                      [style.background-color]="agentChartColor(agent.agentCode)"></div>
                  </div>
                </div>
              </li>
            }
          </ul>
        </div>

        <div class="usage-metrics-panel usage-metrics-panel--total">
          <h4 class="usage-metrics-panel__title">Tokens by Agent</h4>
          <div class="usage-donut-wrap">
            @let donutSegments = usageDonutSegments(metrics);
            <div class="usage-donut">
              <svg class="usage-donut__svg" viewBox="0 0 100 100" aria-hidden="true">
                @if (donutSegments.length) {
                  @for (seg of donutSegments; track seg.agentCode) {
                    <circle
                      class="usage-donut__segment"
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      [attr.stroke]="seg.color"
                      stroke-width="16"
                      [attr.stroke-dasharray]="seg.dashArray"
                      [attr.stroke-dashoffset]="seg.dashOffset"
                      transform="rotate(-90 50 50)"
                      [pTooltip]="seg.agentName"
                      tooltipPosition="top" />
                  }
                } @else {
                  <circle
                    class="usage-donut__segment usage-donut__segment--empty"
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#e2e8f0"
                    stroke-width="16" />
                }
              </svg>
              <div class="usage-donut__hole">
                <span class="usage-donut__total">{{ formatTokens(metrics.totalTokens) }}</span>
                <span class="usage-donut__label">Total tokens</span>
              </div>
            </div>
            <ul class="usage-donut-legend">
              @for (agent of metrics.byAgent; track agent.agentCode) {
                @if (agent.tokens > 0) {
                  <li class="usage-donut-legend__item">
                    <span
                      class="usage-donut-legend__dot"
                      [style.background-color]="agentChartColor(agent.agentCode)"></span>
                    <span class="usage-donut-legend__label">{{ agent.agentName }}</span>
                    <span class="usage-donut-legend__value">{{ formatTokens(agent.tokens) }}</span>
                  </li>
                }
              }
            </ul>
          </div>
        </div>
      </div>

      <div class="usage-metrics-panel usage-metrics-panel--full">
        <h4 class="usage-metrics-panel__title">Top users by tokens</h4>
        <p-table
          [value]="metrics.topUsers"
          dataKey="userId"
          styleClass="usage-summary-table">
          <ng-template pTemplate="header">
            <tr>
              <th>User</th>
              <th>Total tokens</th>
              <th>Top agent</th>
              <th>Agent tokens</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>
                <div class="user-cell">
                  <span class="user-name">{{ row.userName }}</span>
                  @if (row.userEmail) {
                    <span class="user-email">{{ row.userEmail }}</span>
                  }
                </div>
              </td>
              <td>{{ formatTokens(row.totalTokens) }}</td>
              <td>{{ row.topAgentName }}</td>
              <td>{{ formatTokens(row.topAgentTokens) }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="4" class="empty-cell">No usage for the selected filters.</td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    }
  </section>
</p-drawer>
